import Icon from '@mdi/react';
import { GatsbyImage } from 'gatsby-plugin-image';
import AnalyticsPoint from 'gatsby-plugin-purina-analytics/AnalyticsPoint';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import * as React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { mdiCalendar } from 'src/assets/icons/mdiIcons';
import { Link } from 'src/components/Link';
import { Tag } from '../utils/getTagLinks';
import ArticleMedia from './ArticleMedia';
import DotSeparator from './DotSeparator';

interface ArticleHeaderProps {
  node: IArticleContent;
  tags: Tag[];
  date: string;
  mediaType: string;
  shareComponent: React.ReactNode;
}

const ArticleHeader: React.FC<ArticleHeaderProps> = ({
  node,
  tags,
  date,
  mediaType,
  shareComponent,
}) => {
  const { t } = useTranslation();
  const author = node.relationships.field_author;
  const readTime = node.relationships?.field_read_time;

  return (
    <AnalyticsPoint type="module" label="Article title section" typeLabel="article_title_section">
      <Container
        fluid
        className="pds-py-5.5"
        style={{
          background: 'linear-gradient(180deg, #f8f8f8 0%, #f8f8f8 75%, rgba(0,0,0,0) 75%)',
        }}
      >
        <Row className="pds-text-center md:pds-justify-center">
          <Col>
            <div className="pds-mb-5 pds-flex pds-flex-wrap pds-justify-center pds-gap-2 md:pds-gap-4">
              {tags.map((tag, index: number) => (
                <AnalyticsPoint
                  key={`tag-${index}`}
                  type="component"
                  label="article category"
                  typeLabel="article_category"
                  className="pds-flex"
                >
                  <Link
                    className="pds-min-w-[50px] pds-rounded pds-border pds-border-solid pds-border-[#b6bed2] pds-bg-white pds-px-4 pds-py-3 pds-text-body-sm pds-font-medium pds-uppercase pds-tracking-widest pds-text-black pds-no-underline hover:pds-border-black hover:pds-text-black md:pds-text-body-md"
                    to={tag?.href || '/'}
                  >
                    {tag.label}
                  </Link>
                </AnalyticsPoint>
              ))}
            </div>
          </Col>
        </Row>
        <Row className="pds-mb-5.5 pds-text-center md:pds-justify-center">
          <Col xs={12} md={8}>
            <h1 className="pds-text-display-sm pds-font-extrabold" style={{ textWrap: 'balance' }}>
              {node.title}
            </h1>
            <div className="pds-mt-4.5 pds-flex pds-justify-center pds-gap-3">
              {author && (
                <GatsbyImage
                  imgClassName="pds-rounded-circle"
                  className="pds-h-[50px] pds-w-[50px] md:pds-h-[70px] md:pds-w-[70px]"
                  image={author.relationships.image.gatsbyImage}
                  alt={author.image?.alt ?? ''}
                  objectFit="contain"
                  loading="eager"
                />
              )}
              <div className="pds-text-body-[14px] pds-space-y-3 pds-text-left md:pds-text-body-md">
                {author ? (
                  <Link to={author.path.alias}>
                    <span className="pds-text-body-[14px] pds-text-left md:pds-text-body-md">
                      {t('By')} {author.name}
                    </span>
                  </Link>
                ) : null}
                <div className="pds-flex pds-flex-col pds-gap-2 md:pds-flex-row md:pds-items-center">
                  <span className="pds-flex pds-max-w-fit pds-items-center pds-justify-center pds-gap-3">
                    <Icon className="pds-hidden sm:pds-block" path={mdiCalendar} size={0.8} />
                    {t('Updated')}: {date}
                  </span>
                  <DotSeparator />
                  {readTime && (
                    <>
                      <span>{readTime.label}</span>
                      <DotSeparator />
                    </>
                  )}
                  <span className="-pds-ml-[10px]">{shareComponent}</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="pds-justify-center">
          <Col xs={12} md={10} lg={6} className="pds-text-center">
            <ArticleMedia node={node} mediaType={mediaType} />
          </Col>
        </Row>
      </Container>
    </AnalyticsPoint>
  );
};

export default ArticleHeader;
